import { useEffect, useState, useRef, useCallback } from "react";

interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface TourOverlayProps {
  target: string;
  padding?: number | undefined;
  onTargetNotFound?: (() => void) | undefined;
}

export function TourOverlay({ target, padding = 8, onTargetNotFound }: TourOverlayProps) {
  const [rect, setRect] = useState<SpotlightRect | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const rafRef = useRef<number>(0);
  const notFoundTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const updateRect = useCallback((el: Element) => {
    const r = el.getBoundingClientRect();
    setRect({
      top: r.top - padding,
      left: r.left - padding,
      width: r.width + padding * 2,
      height: r.height + padding * 2,
    });
  }, [padding]);

  useEffect(() => {
    // Clear any pending not-found timer
    if (notFoundTimerRef.current) {
      clearTimeout(notFoundTimerRef.current);
      notFoundTimerRef.current = null;
    }

    const findElement = () => document.querySelector(`[data-tour="${target}"]`);

    // Polling with rAF to track position changes (layout shifts, scrolling, etc.)
    let running = true;
    const poll = () => {
      if (!running) return;
      const el = findElement();
      if (el) {
        updateRect(el);
        // Set up ResizeObserver for size changes
        if (!observerRef.current) {
          observerRef.current = new ResizeObserver(() => {
            const fresh = findElement();
            if (fresh) updateRect(fresh);
          });
          observerRef.current.observe(el);
        }
      } else {
        setRect(null);
      }
      rafRef.current = requestAnimationFrame(poll);
    };

    // Start polling
    poll();

    // If element not found after 2s, notify parent
    notFoundTimerRef.current = setTimeout(() => {
      if (!findElement() && onTargetNotFound) {
        onTargetNotFound();
      }
    }, 2000);

    return () => {
      running = false;
      cancelAnimationFrame(rafRef.current);
      observerRef.current?.disconnect();
      observerRef.current = null;
      if (notFoundTimerRef.current) {
        clearTimeout(notFoundTimerRef.current);
      }
    };
  }, [target, padding, updateRect, onTargetNotFound]);

  // For welcome steps with no real target, show a full-page overlay
  if (!rect) {
    return (
      <div
        className="fixed inset-0 z-[200] transition-opacity duration-300"
        style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      />
    );
  }

  return (
    <>
      {/* Full-screen dark overlay */}
      <div className="fixed inset-0 z-[200] pointer-events-none" />

      {/* Spotlight cutout — this is the clickable "window" */}
      <div
        className="fixed z-[201] rounded-lg transition-all duration-300 ease-in-out pointer-events-none"
        style={{
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
          boxShadow: "0 0 0 9999px rgba(0,0,0,0.5)",
        }}
      />
    </>
  );
}
